'use client';

import { useState, useEffect } from 'react';
import { labelsApi, partsApi } from '@parts/api';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/lib/toast';
import { PageState } from '@/components/common/feedback/PageState';
import {
  Printer,
  QrCode,
  Tag,
  Search,
  Plus,
  Download,
  CheckSquare,
  Eye,
} from 'lucide-react';
import QRCode from 'qrcode';

export default function LabelsPage() {
  const { t, locale } = useTranslation();
  const [parts, setParts] = useState<any[]>([]);
  const [selectedParts, setSelectedParts] = useState<Set<string>>(new Set());
  const [labels, setLabels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingParts, setLoadingParts] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [template, setTemplate] = useState('standard');
  const [quantity, setQuantity] = useState(1);
  const [printerName, setPrinterName] = useState('');
  const [previewLabel, setPreviewLabel] = useState<any>(null);
  const [qrCodeUrls, setQrCodeUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    loadParts();
  }, []);

  const loadParts = async () => {
    try {
      setLoadingParts(true);
      const response = await partsApi.list({ limit: 100 });
      setParts(response.items);
    } catch (error) {
      console.error('Failed to load parts:', error);
      toast.error(locale === 'zh' ? '加载零件失败' : 'Failed to load parts');
    } finally {
      setLoadingParts(false);
    }
  };

  const handleSelectPart = (partId: string) => {
    const newSelected = new Set(selectedParts);
    if (newSelected.has(partId)) {
      newSelected.delete(partId);
    } else {
      newSelected.add(partId);
    }
    setSelectedParts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedParts.size === filteredParts.length) {
      setSelectedParts(new Set());
    } else {
      setSelectedParts(new Set(filteredParts.map((p) => p.id)));
    }
  };

  const handleGenerateLabels = async () => {
    if (selectedParts.size === 0) {
      toast.error(locale === 'zh' ? '请先选择零件' : 'Please select parts first');
      return;
    }

    try {
      setLoading(true);
      const response = await labelsApi.bulkGenerate({
        partIds: Array.from(selectedParts),
        template,
        quantityPerPart: quantity,
      });
      const generatedLabels = response.labels;
      setLabels(generatedLabels);
      setSelectedParts(new Set());
      
      // 为每个标签生成二维码图片
      const urls: Record<string, string> = {};
      for (const label of generatedLabels) {
        try {
          const url = await QRCode.toDataURL(label.qrCodeData, {
            width: 200,
            margin: 2,
            color: {
              dark: '#000000',
              light: '#ffffff',
            },
          });
          urls[label.id] = url;
        } catch (err) {
          console.error('Failed to generate QR code:', err);
        }
      }
      setQrCodeUrls(urls);
      
      // 预览第一个标签
      if (generatedLabels.length > 0) {
        setPreviewLabel(generatedLabels[0]);
      }
      
      toast.success(locale === 'zh' ? `已生成 ${generatedLabels.length} 个标签` : `Generated ${generatedLabels.length} labels`);
    } catch (error) {
      console.error('Failed to generate labels:', error);
      toast.error(locale === 'zh' ? '生成标签失败' : 'Failed to generate labels');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintLabels = async () => {
    if (labels.length === 0) return;

    try {
      setLoading(true);
      const labelIds = labels.map((l) => l.id);
      await labelsApi.bulkPrint({
        labelIds,
        printerName: printerName || undefined,
      });
      toast.success(t.labels.printSuccess);
    } catch (error) {
      console.error('Failed to print labels:', error);
      toast.error(locale === 'zh' ? '打印失败' : 'Print failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadLabels = () => {
    labels.forEach((label) => {
      const url = qrCodeUrls[label.id];
      if (url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `label-${label.part?.partNumber || label.labelCode}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    });
    toast.success(locale === 'zh' ? '下载完成' : 'Download complete');
  };

  const filteredParts = parts.filter(
    (part) =>
      part.partNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      part.partNameEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (part.partNameCn && part.partNameCn.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{'标签管理'}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {locale === 'zh' ? '生成和打印零件标签' : 'Generate and print part labels'}
          </p>
        </div>
        <div className="flex gap-3">
          {labels.length > 0 && (
            <>
              <Button variant="outline" onClick={handleDownloadLabels}>
                <Download className="w-4 h-4 mr-2" />
                {'下载'}
              </Button>
              <Button onClick={handlePrintLabels} disabled={loading}>
                <Printer className="w-4 h-4 mr-2" />
                {'打印'}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Part Selection */}
        <div className="lg:col-span-2 space-y-6">
          {/* Search & Actions */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder={'搜索零件...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" onClick={handleSelectAll}>
                <CheckSquare className="w-4 h-4 mr-2" />
                {selectedParts.size === filteredParts.length && filteredParts.length > 0
                  ? ('取消全选')
                  : ('全选')}
              </Button>
              <Button
                onClick={handleGenerateLabels}
                disabled={selectedParts.size === 0 || loading}
              >
                <Plus className="w-4 h-4 mr-2" />
                {'生成标签'}
                {selectedParts.size > 0 && ` (${selectedParts.size})`}
              </Button>
            </div>
          </div>

          {/* Parts List */}
          {loadingParts ? (
            <PageState
              variant="loading"
              title={t.common.loading}
              layout="card"
              className="mx-auto w-full max-w-2xl"
            />
          ) : filteredParts.length === 0 ? (
            <PageState
              variant="empty"
              title={t.parts.labels.emptyTitle}
              description={t.parts.labels.emptyDesc}
              layout="card"
              className="mx-auto w-full max-w-2xl"
            />
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="max-h-[500px] overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="w-12 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedParts.size === filteredParts.length && filteredParts.length > 0}
                          onChange={handleSelectAll}
                          className="rounded border-gray-300"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {'零件号'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {'名称'}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        {'库存'}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredParts.map((part) => (
                      <tr
                        key={part.id}
                        className={`hover:bg-gray-50 cursor-pointer ${
                          selectedParts.has(part.id) ? 'bg-blue-50' : ''
                        }`}
                        onClick={() => handleSelectPart(part.id)}
                      >
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedParts.has(part.id)}
                            onChange={() => handleSelectPart(part.id)}
                            className="rounded border-gray-300"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900 font-mono">
                            {part.partNumber}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm text-gray-900">
                            {locale === 'zh' ? (part.partNameCn || part.partNameEn) : part.partNameEn}
                          </p>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {part.currentStock} {part.unit}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Settings & Preview */}
        <div className="space-y-6">
          {/* Label Settings */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {locale === 'zh' ? '标签设置' : 'Label Settings'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {'模板'}
                </label>
                <select
                  value={template}
                  onChange={(e) => setTemplate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm"
                >
                  <option value="standard">{'标准'}</option>
                  <option value="compact">{'紧凑'}</option>
                  <option value="detailed">{'详细'}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {'尺寸'}
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm">
                  <option value="50x30">50mm × 30mm</option>
                  <option value="100x50">100mm × 50mm</option>
                  <option value="150x100">150mm × 100mm</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {'每个零件生成数量'}
                </label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {'打印机'}
                  <span className="text-gray-400 font-normal ml-1">({'可选'})</span>
                </label>
                <Input
                  type="text"
                  placeholder={locale === 'zh' ? '输入打印机名称' : 'Enter printer name'}
                  value={printerName}
                  onChange={(e) => setPrinterName(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Generated Labels List */}
          {labels.length > 0 && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {locale === 'zh' ? '已生成标签' : 'Generated Labels'} ({labels.length})
              </h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {labels.map((label) => (
                  <div
                    key={label.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors flex items-center justify-between ${
                      previewLabel?.id === label.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => setPreviewLabel(label)}
                  >
                    <div className="flex items-center min-w-0">
                      <Tag className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {label.part?.partNumber}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {label.part?.partNameEn}
                        </p>
                      </div>
                    </div>
                    {qrCodeUrls[label.id] && (
                      <img
                        src={qrCodeUrls[label.id]}
                        alt="QR"
                        className="w-10 h-10 flex-shrink-0 ml-2"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Label Preview */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {'预览'}
            </h3>
            {previewLabel ? (
              <div>
                {/* 标签预览卡片 */}
                <div className="bg-white border-2 border-gray-200 rounded-lg p-4 space-y-3">
                  {/* Part Number */}
                  <div className="text-center border-b pb-2">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Part Number</p>
                    <p className="text-lg font-bold text-gray-900 font-mono">
                      {previewLabel.part?.partNumber}
                    </p>
                  </div>
                  
                  {/* English Name */}
                  <div className="text-center">
                    <p className="text-[10px] text-gray-400 uppercase tracking-wider">Name</p>
                    <p className="text-sm font-medium text-gray-700 line-clamp-2">
                      {previewLabel.part?.partNameEn}
                    </p>
                  </div>
                  
                  {/* 二维码 */}
                  <div className="flex justify-center py-2">
                    {qrCodeUrls[previewLabel.id] ? (
                      <img
                        src={qrCodeUrls[previewLabel.id]}
                        alt="QR Code"
                        className="w-24 h-24"
                      />
                    ) : (
                      <QrCode className="w-24 h-24 text-gray-300" />
                    )}
                  </div>
                  
                  {/* 创建时间 */}
                  <div className="text-center border-t pt-2">
                    <p className="text-[10px] text-gray-400">
                      {new Date(previewLabel.createdAt).toLocaleString(locale === 'zh' ? 'zh-CN' : 'en-US')}
                    </p>
                  </div>
                </div>
                
                {/* 二维码内容 */}
                <div className="mt-3 p-2 bg-gray-50 rounded text-xs">
                  <p className="text-gray-500 mb-1">{locale === 'zh' ? '二维码内容：' : 'QR Content:'}</p>
                  <code className="block bg-gray-100 p-2 rounded text-xs text-gray-600 break-all">
                    {previewLabel.qrCodeData}
                  </code>
                </div>
                
                {/* 下载按钮 */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-3"
                  onClick={() => {
                    const url = qrCodeUrls[previewLabel.id];
                    if (url) {
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `label-${previewLabel.part?.partNumber || previewLabel.labelCode}.png`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  {'下载标签'}
                </Button>
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-200 rounded-lg p-8 text-center">
                <QrCode className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">
                  {locale === 'zh' ? '选择零件并生成标签后预览' : 'Generate labels to preview'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
